import { PlayerState, OrbState, ArenaState, GamePhase } from '@push.io/shared';
import type { PublicPlayerAppearance } from '@push.io/shared';

const DEFAULT_APPEARANCE: PublicPlayerAppearance = {
  baseColorId: 'base_color_blue',
  skinId: null, patternId: null, flagId: null, letterId: null,
  accessoryId: null, trailId: null, nameplateId: null, badgeId: null, victoryEffectId: null,
};

export class GameState {
  players: Map<string, PlayerState> = new Map();
  orbs: OrbState[] = [];
  arena: ArenaState = {
    centerX: 0, centerY: 0, radius: 40, initialRadius: 40, minRadius: 8,
    brokenSegments: [], collapsePercent: 0,
  };
  phase: GamePhase = 'lobby';
  tick: number = 0;
  playerId: string = '';
  localPlayer: PlayerState | null = null;
  playerAppearances: Map<string, PublicPlayerAppearance> = new Map();

  update(_delta?: number): void {
    void _delta;
    this.tick++;
  }

  applyPlayerAppearances(appearances: Array<{ playerId: string; appearance: PublicPlayerAppearance }>): void {
    for (const { playerId, appearance } of appearances) {
      this.playerAppearances.set(playerId, appearance);
      const player = this.players.get(playerId);
      if (player) {
        player.appearance = appearance;
      }
    }
  }

  applySnapshot(snapshot: any): void {
    this.tick = snapshot.tick;

    if (snapshot.players) {
      const snapshotPlayerIds = new Set<string>(snapshot.players.map((player: { id: string }) => player.id));
      for (const id of this.players.keys()) {
        if (!snapshotPlayerIds.has(id)) this.players.delete(id);
      }
      for (const playerData of snapshot.players) {
        const existing = this.players.get(playerData.id);
        const storedAppearance = this.playerAppearances.get(playerData.id) ?? DEFAULT_APPEARANCE;
        if (existing) {
          const authoritativeSequence = playerData.sequence ?? 0;
          existing.x = playerData.x;
          existing.y = playerData.y;
          existing.vx = playerData.vx;
          existing.vy = playerData.vy;
          existing.angle = playerData.angle;
          existing.alive = playerData.alive;
          existing.knockbackTicks = playerData.knockbackTicks || 0;
          existing.orbs = playerData.orbs;
          existing.eliminated = playerData.eliminated || false;
          existing.placement = playerData.placement || 0;
          existing.sequence = authoritativeSequence;
          existing.appearance = storedAppearance;
          if (playerData.upgrades) {
            existing.upgrades = playerData.upgrades;
          }
        } else {
          this.players.set(playerData.id, {
            id: playerData.id,
            name: playerData.name || '',
            x: playerData.x, y: playerData.y,
            vx: playerData.vx || 0, vy: playerData.vy || 0,
            angle: playerData.angle || 0,
            radius: playerData.radius || 0.5,
            alive: playerData.alive !== false,
            orbs: playerData.orbs || 0,
            upgrades: playerData.upgrades || { push: 0, weight: 0, speed: 0, control: 0 },
            desiredDirX: 0, desiredDirY: 0,
            knockbackTicks: playerData.knockbackTicks || 0,
            impactImmunityUntilTick: 0, recoveryTimer: 0, sequence: 0,
            appearance: storedAppearance,
            eliminated: !playerData.alive, placement: 0,
          });
        }
      }
    }

    if (snapshot.orbs) {
      this.orbs = snapshot.orbs.map((o: any) => ({
        id: o.id, x: o.x, y: o.y, radius: 0.3,
        collected: false, spawnTick: 0, value: 1,
      }));
    }

    if (snapshot.arena) {
      this.arena.radius = snapshot.arena.radius;
      this.arena.brokenSegments = snapshot.arena.segments || [];
    }

    this.localPlayer = this.playerId ? this.players.get(this.playerId) || null : null;
  }

  setPlayerId(id: string): void {
    this.playerId = id;
    this.localPlayer = id ? this.players.get(id) || null : null;
  }

  resetForServer(playerId: string): void {
    this.players.clear();
    this.orbs = [];
    this.phase = 'lobby';
    this.tick = 0;
    this.arena = {
      centerX: 0, centerY: 0, radius: 40, initialRadius: 40, minRadius: 8,
      brokenSegments: [], collapsePercent: 0,
    };
    this.playerId = playerId;
    this.localPlayer = null;
    this.playerAppearances.clear();
  }

  getLocalPlayer(): PlayerState | null {
    return this.localPlayer;
  }

  getAlivePlayers(): PlayerState[] {
    return Array.from(this.players.values()).filter((p) => p.alive);
  }
}
